import * as ImagePicker from 'expo-image-picker';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Camera,
  Check,
  ChevronDown,
  ChevronLeft,
  Plus,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  Image,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useConfirm } from '@/components/ConfirmDialog';
import { PhotoLightbox } from '@/components/PhotoLightbox';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { parseLocalDate, toLocalYMD } from '@/lib/dates';
import { goBackOrTo } from '@/lib/nav';
import type { InventoryItemRow, ItemCondition } from '@/db/types';
import {
  COMMON_ROOMS,
  CONDITION_OPTIONS,
  INVENTORY_CATEGORIES,
  deleteInventoryItem,
  getInventoryItem,
  parseExtraPhotos,
  parsePhotoCaptions,
  updateInventoryItem,
} from '@/lib/inventory';
import { persistPickedFile } from '@/lib/files';
import { webNoOutline } from '@/lib/web-style';

export default function EditInventoryItem() {
  const router = useRouter();
  const db = useSQLiteContext();
  const confirm = useConfirm();
  const { id } = useLocalSearchParams<{ id: string }>();

  const [row, setRow] = useState<InventoryItemRow | null>(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [room, setRoom] = useState<string | null>(null);
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [serial, setSerial] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [currentValue, setCurrentValue] = useState('');
  const [condition, setCondition] = useState<ItemCondition | null>(null);
  const [vendor, setVendor] = useState('');
  const [isInsured, setIsInsured] = useState(false);
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [extraPhotos, setExtraPhotos] = useState<string[]>([]);
  const [captions, setCaptions] = useState<Record<string, string>>({});
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const [catOpen, setCatOpen] = useState(false);
  const [roomOpen, setRoomOpen] = useState(false);
  const [condOpen, setCondOpen] = useState(false);

  useEffect(() => {
    (async () => {
      if (!id) return;
      const r = await getInventoryItem(db, id);
      if (!r) return;
      setRow(r);
      setName(r.name);
      setCategory(r.category);
      setRoom(r.room);
      setBrand(r.brand ?? '');
      setModel(r.model ?? '');
      setSerial(r.serial_number ?? '');
      setPurchaseDate(r.purchase_date ? toLocalYMD(r.purchase_date) : '');
      setPurchasePrice(r.purchase_cents ? (r.purchase_cents / 100).toFixed(2) : '');
      setCurrentValue(r.value_cents ? (r.value_cents / 100).toFixed(2) : '');
      setCondition(r.condition);
      setVendor(r.vendor ?? '');
      setIsInsured(r.is_insured === 1);
      setNotes(r.notes ?? '');
      setPhoto(r.photo_uri);
      setExtraPhotos(parseExtraPhotos(r.extra_photos_json));
      setCaptions(parsePhotoCaptions(r.photo_captions_json));
    })();
  }, [db, id]);

  if (!row) return <View style={styles.container} />;

  const pickPhoto = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
    });
    if (res.canceled || !res.assets[0]) return;
    const a = res.assets[0];
    const persisted = await persistPickedFile(
      a.uri,
      a.fileName ?? `item-${Date.now()}.jpg`
    );
    setPhoto(persisted);
  };

  const addExtraPhoto = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
      allowsMultipleSelection: true,
    });
    if (res.canceled || !res.assets.length) return;
    const next: string[] = [...extraPhotos];
    for (const a of res.assets) {
      const persisted = await persistPickedFile(
        a.uri,
        a.fileName ?? `item-${Date.now()}.jpg`
      );
      next.push(persisted);
    }
    setExtraPhotos(next);
  };

  const removeExtra = (i: number) => {
    setExtraPhotos((arr) => arr.filter((_, idx) => idx !== i));
  };

  const promoteExtra = (i: number) => {
    // Make this extra photo the primary, and demote the old primary into
    // the extras so nothing is lost.
    const newPrimary = extraPhotos[i];
    const remaining = extraPhotos.filter((_, idx) => idx !== i);
    if (photo) remaining.unshift(photo);
    setPhoto(newPrimary);
    setExtraPhotos(remaining);
  };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await updateInventoryItem(db, row.id, {
        name,
        category,
        room,
        brand: brand.trim() || null,
        model: model.trim() || null,
        serial_number: serial.trim() || null,
        purchase_date: parseDate(purchaseDate),
        purchase_cents: parseCents(purchasePrice),
        value_cents: parseCents(currentValue),
        condition,
        vendor: vendor.trim() || null,
        is_insured: isInsured ? 1 : 0,
        photo_uri: photo,
        extra_photos: extraPhotos,
        photo_captions: captions,
        notes: notes.trim() || null,
      });
      goBackOrTo(router, '/inventory');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await deleteInventoryItem(db, row.id);
      goBackOrTo(router, '/inventory');
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async () => {
    const ok = await confirm({
      title: 'Remove item?',
      message: 'It will disappear from inventory totals. Photos and receipts are kept.',
      destructive: true,
      confirmLabel: 'Remove',
    });
    if (ok) await remove();
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/inventory')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Edit item</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 80 }} showsVerticalScrollIndicator={false}>
        <Pressable onPress={pickPhoto} style={styles.photoBtn}>
          {photo ? (
            <Image source={{ uri: photo }} style={styles.photo} />
          ) : (
            <View style={styles.photoEmpty}>
              <Camera size={32} color={Palette.inkFaint} strokeWidth={1.6} />
              <Text style={styles.photoLabel}>Tap to add the main photo</Text>
            </View>
          )}
        </Pressable>

        <View style={styles.galleryWrap}>
          <View style={styles.galleryHead}>
            <Text style={styles.galleryTitle}>
              Photo gallery · {extraPhotos.length}
            </Text>
            <Pressable
              onPress={addExtraPhoto}
              style={({ pressed }) => [styles.galleryAdd, pressed && { opacity: 0.85 }]}>
              <Plus size={14} color={Palette.cream} strokeWidth={2.2} />
              <Text style={styles.galleryAddText}>Add</Text>
            </Pressable>
          </View>
          {extraPhotos.length === 0 ? (
            <Text style={styles.galleryEmpty}>
              Add receipts, serial-plate close-ups, or condition shots. Tap a
              photo to view it full-size and add a caption (serial #, condition,
              etc.); long-press to promote it to the main photo.
            </Text>
          ) : (
            <View style={styles.galleryGrid}>
              {extraPhotos.map((uri, i) => (
                <View key={`${uri}-${i}`} style={styles.galleryCell}>
                  <Pressable
                    onPress={() => setLightboxIndex(i)}
                    onLongPress={() => promoteExtra(i)}
                    delayLongPress={350}>
                    <Image source={{ uri }} style={styles.galleryThumb} />
                  </Pressable>
                  {captions[uri] ? (
                    <Text style={styles.captionTag} numberOfLines={2}>
                      {captions[uri]}
                    </Text>
                  ) : null}
                  <Pressable
                    onPress={() => removeExtra(i)}
                    hitSlop={6}
                    style={({ pressed }) => [styles.galleryRemove, pressed && { opacity: 0.7 }]}>
                    <X size={11} color={Palette.cream} strokeWidth={2.4} />
                  </Pressable>
                </View>
              ))}
            </View>
          )}
        </View>

        <Section title="BASICS">
          <Field label="Name" value={name} onChangeText={setName} placeholder="" />
          <Pick label="Category" value={category} placeholder="Tap to pick" onPress={() => setCatOpen(true)} />
          <Pick label="Room" value={room} placeholder="Where is it?" onPress={() => setRoomOpen(true)} />
        </Section>

        <Section title="MAKE & MODEL">
          <Field label="Brand" value={brand} onChangeText={setBrand} placeholder="" />
          <Field label="Model #" value={model} onChangeText={setModel} placeholder="" />
          <Field label="Serial #" value={serial} onChangeText={setSerial} placeholder="" />
        </Section>

        <Section title="PURCHASE">
          <Field label="Purchased (YYYY-MM-DD)" value={purchaseDate} onChangeText={setPurchaseDate} placeholder="" />
          <Field label="Price paid" value={purchasePrice} onChangeText={setPurchasePrice} placeholder="" inputMode="decimal" />
          <Field label="Current value" value={currentValue} onChangeText={setCurrentValue} placeholder="" inputMode="decimal" />
          <Field label="Where you got it" value={vendor} onChangeText={setVendor} placeholder="" />
        </Section>

        <Section title="CONDITION & COVERAGE">
          <Pick
            label="Condition"
            value={condition ? CONDITION_OPTIONS.find((c) => c.v === condition)?.label ?? null : null}
            placeholder="Tap to pick"
            onPress={() => setCondOpen(true)}
          />
          <Pressable onPress={() => setIsInsured(!isInsured)} style={styles.insuredRow}>
            <View style={[styles.insuredIcon, isInsured && { backgroundColor: Palette.forest }]}>
              <ShieldCheck
                size={16}
                color={isInsured ? Palette.cream : Palette.forest}
                strokeWidth={1.8}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.insuredLabel}>Insured under policy</Text>
            </View>
            <View style={[styles.toggle, isInsured && styles.toggleOn]}>
              <View style={[styles.toggleThumb, isInsured && styles.toggleThumbOn]} />
            </View>
          </Pressable>
        </Section>

        <Section title="NOTES">
          <TextInput
            value={notes}
            onChangeText={setNotes}
            multiline
            placeholderTextColor={Palette.inkFaint}
            style={[styles.fieldInput, webNoOutline, { height: 88, textAlignVertical: 'top' }]}
          />
        </Section>

        <Pressable
          onPress={save}
          disabled={saving}
          style={({ pressed }) => [
            styles.saveBtn,
            saving && { opacity: 0.6 },
            pressed && { transform: [{ scale: 0.98 }] },
          ]}>
          <Check size={18} color={Palette.cream} strokeWidth={2} />
          <Text style={styles.saveText}>{saving ? 'Saving…' : 'Save changes'}</Text>
        </Pressable>

        <Pressable onPress={confirmRemove} style={({ pressed }) => [styles.removeBtn, pressed && { opacity: 0.85 }]}>
          <Trash2 size={16} color={Palette.clay} strokeWidth={1.8} />
          <Text style={styles.removeText}>Remove from inventory</Text>
        </Pressable>
      </ScrollView>

      <PickerModal
        open={catOpen}
        title="Category"
        options={INVENTORY_CATEGORIES.slice()}
        current={category}
        onPick={(v) => {
          setCategory(v);
          setCatOpen(false);
        }}
        onClose={() => setCatOpen(false)}
      />
      <PickerModal
        open={roomOpen}
        title="Room"
        options={COMMON_ROOMS.slice()}
        current={room}
        onPick={(v) => {
          setRoom(v);
          setRoomOpen(false);
        }}
        onClose={() => setRoomOpen(false)}
      />
      <PickerModal
        open={condOpen}
        title="Condition"
        options={CONDITION_OPTIONS.map((c) => c.label)}
        current={condition ? CONDITION_OPTIONS.find((c) => c.v === condition)?.label ?? null : null}
        onPick={(label) => {
          const found = CONDITION_OPTIONS.find((c) => c.label === label);
          setCondition(found?.v ?? null);
          setCondOpen(false);
        }}
        onClose={() => setCondOpen(false)}
      />

      <PhotoLightbox
        uris={lightboxIndex != null ? extraPhotos : null}
        startIndex={lightboxIndex ?? 0}
        captions={captions}
        onCaptionChange={(uri, v) =>
          setCaptions((prev) => {
            const next = { ...prev };
            if (v.trim().length === 0) delete next[uri];
            else next[uri] = v;
            return next;
          })
        }
        onClose={() => setLightboxIndex(null)}
      />
    </SafeAreaView>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function Field({
  label,
  value,
  onChangeText,
  placeholder,
  inputMode = 'text',
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'numeric' | 'decimal';
}) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={Palette.inkFaint}
        inputMode={inputMode}
        style={[styles.fieldInput, webNoOutline]}
      />
    </View>
  );
}

function Pick({
  label,
  value,
  placeholder,
  onPress,
}: {
  label: string;
  value: string | null;
  placeholder: string;
  onPress: () => void;
}) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <Pressable
        onPress={onPress}
        style={({ pressed }) => [styles.pickBox, pressed && { opacity: 0.92 }]}>
        <Text style={[styles.pickText, !value && styles.pickPlaceholder]}>
          {value ?? placeholder}
        </Text>
        <ChevronDown size={14} color={Palette.inkFaint} strokeWidth={1.7} />
      </Pressable>
    </View>
  );
}

function PickerModal({
  open,
  title,
  options,
  current,
  onPick,
  onClose,
}: {
  open: boolean;
  title: string;
  options: string[];
  current: string | null;
  onPick: (v: string) => void;
  onClose: () => void;
}) {
  return (
    <Modal animationType="fade" transparent visible={open} onRequestClose={onClose}>
      <Pressable style={styles.modalBackdrop} onPress={onClose}>
        <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
          <Text style={styles.modalTitle}>{title}</Text>
          <ScrollView style={{ maxHeight: 380 }}>
            {options.map((o) => {
              const sel = current === o;
              return (
                <Pressable
                  key={o}
                  onPress={() => onPick(o)}
                  style={({ pressed }) => [
                    styles.modalRow,
                    pressed && { backgroundColor: '#EEF3EE' },
                    sel && { backgroundColor: '#EEF3EE' },
                  ]}>
                  <Text style={styles.modalRowText}>{o}</Text>
                </Pressable>
              );
            })}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function parseCents(raw: string): number | null {
  const s = raw.replace(/[^0-9.]/g, '');
  if (!s) return null;
  const n = parseFloat(s);
  if (isNaN(n)) return null;
  return Math.round(n * 100);
}

function parseDate(raw: string): number | null {
  const t = parseLocalDate(raw);
  if (isNaN(t)) return null;
  return t;
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  photoBtn: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    aspectRatio: 4 / 3,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    overflow: 'hidden',
  },
  photo: { width: '100%', height: '100%' },
  photoEmpty: {
    flex: 1,
    alignItems: 'center', justifyContent: 'center',
    gap: 6, padding: 18,
  },
  photoLabel: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.inkSoft },
  section: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, padding: Spacing.four,
  },
  sectionTitle: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint,
    letterSpacing: 0.6,
  },
  fieldLabel: {
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkSoft,
    letterSpacing: 0.4,
  },
  fieldInput: {
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 11,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
  },
  pickBox: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 12,
  },
  pickText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  pickPlaceholder: { color: Palette.inkFaint, fontFamily: Fonts.sansMedium },
  insuredRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: Spacing.three,
    padding: 12,
    backgroundColor: Palette.sand,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Palette.line,
  },
  insuredIcon: {
    width: 32, height: 32, borderRadius: 16,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  insuredLabel: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  toggle: {
    width: 40, height: 24, borderRadius: 12,
    backgroundColor: Palette.sandDeep, padding: 3, justifyContent: 'center',
  },
  toggleOn: { backgroundColor: Palette.forest },
  toggleThumb: { width: 18, height: 18, borderRadius: 9, backgroundColor: Palette.cream },
  toggleThumbOn: { transform: [{ translateX: 16 }] },
  saveBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, backgroundColor: Palette.forest,
    paddingVertical: 16, borderRadius: 18,
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
  },
  saveText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 15.5 },
  removeBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, paddingVertical: 14,
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
  },
  removeText: { color: Palette.clay, fontFamily: Fonts.sansBold, fontSize: 13.5 },
  modalBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand, borderRadius: 24,
    padding: 18, width: '100%', maxWidth: 380,
  },
  modalTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink, marginBottom: 10 },
  modalRow: { paddingVertical: 11, paddingHorizontal: 12, borderRadius: 10 },
  modalRowText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  galleryWrap: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: Spacing.four,
  },
  galleryHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  galleryTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.inkFaint,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  galleryAdd: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: Palette.forest,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
  },
  galleryAddText: {
    color: Palette.cream,
    fontFamily: Fonts.sansBold,
    fontSize: 12.5,
    letterSpacing: 0.2,
  },
  galleryEmpty: {
    marginTop: 8,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    lineHeight: 17,
  },
  galleryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 10,
  },
  galleryCell: {
    width: 72,
    position: 'relative',
  },
  captionTag: {
    marginTop: 4,
    fontFamily: Fonts.sansSemibold,
    fontSize: 11,
    color: Palette.inkSoft,
    lineHeight: 12,
  },
  galleryThumb: {
    width: 72,
    height: 72,
    borderRadius: 10,
    backgroundColor: '#EAF0EA',
  },
  galleryRemove: {
    position: 'absolute',
    top: 3,
    right: 3,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: 'rgba(42,37,31,0.7)',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
